import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Sparkles } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';
import { ImageSlot } from '../components/ImageSlot';
import { roasters } from '../roasters/roasters';
const SITE_URL = 'https://www.groundedlivermore.com';
const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Our Roasters: California Coffee, Curated',
  description: "Grounded is a multi-roaster coffee shop in Livermore, CA. We curate beans from California's best independent roasters - Proyecto Diaz, Chromatic, Temple, Cat & Cloud, and Daybreak.",
  url: `${SITE_URL}/roasters`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Grounded Livermore',
    url: SITE_URL
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: roasters.map((roaster, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: roaster.name,
      url: `${SITE_URL}/roasters/${roaster.slug}`
    }))
  }
};
export function Roasters() {
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-788" data-mp="25q2sq4medju6">
      <SEO title="Our Roasters - California Coffee, Curated | Grounded Livermore" description="Grounded is a multi-roaster coffee shop in Livermore, CA. We curate beans from California's best independent roasters - Proyecto Diaz, Chromatic, Temple, Cat & Cloud, and Daybreak. Meet them here." canonical={`${SITE_URL}/roasters`} structuredData={collectionSchema} data-id="element-789" data-mp="20rj39u8lhpx2" />
      <HamburgerMenu data-id="element-790" data-mp="2kfrx08pse3yo" />

      <main data-id="element-791" data-mp="2ke75kryrr151">
        {/* Header */}
        <header className="pt-32 pb-16 px-6 md:px-12 lg:px-24 max-w-5xl mx-auto" data-id="element-792" data-mp="2khbdo0fcuh0z">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-793" data-mp="1d388n8thsmh8">
            <div className="flex items-center gap-3 mb-6" data-id="element-794" data-mp="1aik4jtf3hxnh">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-795" data-mp="1fg244t7830it" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-796" data-mp="1ffka83818ye2">
                Multi-Roaster Shop
              </span>
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-10" data-id="element-797" data-mp="15guqe4wkt3v6">
              Our Roasters: California Coffee, Curated
            </h1>

            <div className="space-y-6 max-w-3xl" data-id="element-798" data-mp="wh7q5mfl243p">
              <p className="font-inter text-lg text-white/70 leading-relaxed border-l-2 border-[#D97E3A] pl-6" data-id="element-799" data-mp="wj0yr79rayec">
                Most coffee shops roast one thing. We do it differently.
                Grounded is a multi-roaster shop - instead of putting our name on
                a single roast, we curate beans from some of the best
                independent roasters in California and rotate them across our
                drip, espresso, cold brew, and retail shelf. Think of it as a
                corner view into the state's coffee.
              </p>
              <p className="font-inter text-lg text-white/70 leading-relaxed pl-6" data-id="element-800" data-mp="3lc9c5m51w79z">
                Why curate instead of roast our own? Because it keeps the bar
                exciting, it lets us champion small and family-owned roasters we
                genuinely believe in, and it means there's always something new
                in your cup. When you sit down at Grounded, you're getting a
                rotating tour of California craft.
              </p>
            </div>
          </motion.div>
        </header>

        {/* Roaster cards */}
        <section aria-label="Roasters we feature" className="px-6 md:px-12 lg:px-24 max-w-5xl mx-auto pb-20" data-id="element-801" data-mp="ly6y839yqmik">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-802" data-mp="vtqskl8vpd57">
            {roasters.map((roaster, i) => <motion.li key={roaster.slug} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.07
          }} className="last:md:col-span-2" data-id="element-803" data-mp="qwi6i9km0oyo" data-mpi={i}>
                <article className="h-full border border-white/10 bg-[#111] transition-colors hover:border-[#D97E3A]/40 flex flex-col" data-id="element-804" data-mp="1jqnd0juwr0c1">
                  <ImageSlot src={roaster.image} alt={roaster.imageAlt} label={`${roaster.name} photo`} aspect="aspect-[16/9]" fit="contain" data-id="element-805" data-mp="ryz3s67fetxf" />

                  <div className="p-8 flex flex-col flex-grow" data-id="element-806" data-mp="10y2plhxz9nfm">
                    <div className="flex items-center gap-2 text-[#D97E3A] font-inter text-xs font-medium uppercase tracking-widest mb-3" data-id="element-807" data-mp="15zi9v7sbzpwy">
                      <MapPin size={14} aria-hidden="true" data-id="element-808" data-mp="2cgnavyqzrd5e" />
                      {roaster.location}
                    </div>

                    <h2 className="font-bebas text-3xl md:text-4xl leading-none mb-3" data-id="element-809" data-mp="5q0rodb6ij5z">
                      <Link to={`/roasters/${roaster.slug}`} className="hover:text-[#D97E3A] transition-colors" data-id="element-810" data-mp="r7c8fcc0i36">
                        {roaster.name}
                      </Link>
                    </h2>

                    <p className="font-inter text-base text-white/70 leading-relaxed mb-2" data-id="element-811" data-mp="3n6idmygu8mob">
                      {roaster.cardSummary}
                    </p>
                    <p className="font-inter text-sm text-white/45 leading-relaxed mb-6 flex-grow" data-id="element-812" data-mp="3e5u4oi6axcia">
                      {roaster.cardRole}
                    </p>

                    <Link to={`/roasters/${roaster.slug}`} className="inline-flex items-center gap-2 font-bebas text-lg tracking-wide text-[#D97E3A] group self-start" data-id="element-813" data-mp="347xovd2f5tdu">
                      MEET {roaster.name.toUpperCase()}
                      <ArrowRight size={18} aria-hidden="true" className="group-hover:translate-x-1 transition-transform" data-id="element-814" data-mp="12eqb482gfju2" />
                    </Link>
                  </div>
                </article>
              </motion.li>)}
          </ul>
        </section>

        {/* Guest roasters */}
        <section aria-labelledby="guests-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10" data-id="element-815" data-mp="2rvc0m1f1d298">
          <div className="max-w-3xl mx-auto text-center" data-id="element-816" data-mp="3kl13ylf7xee0">
            <Sparkles className="text-[#D97E3A] mx-auto mb-6" size={36} strokeWidth={1.5} aria-hidden="true" data-id="element-817" data-mp="36mupyptsc959" />
            <h2 id="guests-heading" className="font-bebas text-4xl md:text-5xl leading-none mb-6" data-id="element-818" data-mp="3aq0t3ollpk7i">
              And a Rotating Cast of Guests
            </h2>
            <p className="font-inter text-lg text-white/70 leading-relaxed" data-id="element-819" data-mp="1z6obfur61sqe">
              Our five regulars are just the beginning. We also rotate in
              coffees from other distinct small-batch and microroasteries -
              names like Pause and Pulse, Little Wolf, and other incredible
              artisans near and far - as one-off features on our pour-over bar
              and retail shelf. Ask us who's guesting this week.
            </p>
          </div>
        </section>

        <NewsletterSignup data-id="element-820" data-mp="18ww5ysypjayo" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-821" data-mp="1405r28yn2leu">
          <p className="font-bebas text-2xl text-white/50" data-id="element-822" data-mp="13y2t92r8vnzk">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}